import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import SectionSubscribe2 from "components/SectionSubscribe2/SectionSubscribe2";
import SocialsList from "shared/SocialsList/SocialsList";
import Label from "components/Label/Label";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SectionClientSay from "components/SectionClientSay/SectionClientSay";
import BackgroundSection from "components/BackgroundSection/BackgroundSection";
import { sendNewsLetterEmail } from "utils/apiUtil";
import toast from "react-hot-toast";
import { globalJson } from "global/global_json";
import { ContactFun } from "api/profile/profile";
import { Link } from "react-router-dom";
import RentalPolicies from "components/RentalPolicies/RentalPolicies";

export interface PageContactProps {
  className?: string;
}

const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const [getEmail, setEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");
  const currentHour = new Date().getHours();
  const isOpen = currentHour < 19;
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const sendEmail = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!getEmail.trim() || !emailRegex.test(getEmail)) {
      toast.error("Please enter a valid email address");
      setLoading(false);
      return;
    }

    if (getEmail.length === 0 || newName.length === 0 || message.length === 0) {
      toast.error("Fields are required!");
      setLoading(false);
      return;
    }

    // Create the payload with name, email, and message
    const companyPayload = {
      comapny:
        process.env.REACT_APP_ENV !== "prod"
          ? globalJson.dev_company_id
          : globalJson.prod_company_id,
      name: newName,
      email: getEmail,
      message: message,
    };
    const renterPayload = {
      name: newName,
      email: getEmail,
      message: message,
    };

    const result = await ContactFun(companyPayload);
    const result1 = await ContactFun(renterPayload);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success("Sent successfully!");
      setEmail("");
      setNewName("");
      setMessage("");
    }

    setLoading(false);
  };

  useEffect(() => {
    const openingHour = 9; // 9:00 AM
    const closingHour = 19; // 7:00 PM

    const updateRemainingTime = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentSeconds = now.getSeconds();

      let displayTime: string;

      if (currentHour >= openingHour && currentHour < closingHour) {
        // Business is OPEN: Calculate countdown to closing
        const remainingTimeInSeconds =
          (closingHour - currentHour) * 3600 -
          currentMinutes * 60 -
          currentSeconds;

        const remainingHours = Math.floor(remainingTimeInSeconds / 3600);
        const remainingMinutes = Math.floor(
          (remainingTimeInSeconds % 3600) / 60
        );
        const remainingSeconds = remainingTimeInSeconds % 60;

        displayTime = `Closing in ${remainingHours}h ${remainingMinutes}m ${remainingSeconds}s`;
      } else {
        // Business is CLOSED: Calculate countdown to opening (Next 9:00 AM)
        let openingTime = new Date();
        openingTime.setHours(openingHour, 0, 0, 0); // Set time to next 9:00 AM

        if (currentHour >= closingHour) {
          // If it's past 7 PM, next opening is **tomorrow at 9 AM**
          openingTime.setDate(openingTime.getDate() + 1);
        }

        const remainingTimeInSeconds = Math.floor(
          (openingTime.getTime() - now.getTime()) / 1000
        );
        const remainingHours = Math.floor(remainingTimeInSeconds / 3600);
        const remainingMinutes = Math.floor(
          (remainingTimeInSeconds % 3600) / 60
        );
        const remainingSeconds = remainingTimeInSeconds % 60;

        displayTime = `Opening in ${remainingHours}h ${remainingMinutes}m ${remainingSeconds}s`;
      }

      setTimeLeft(displayTime);
    };

    // Initial update
    updateRemainingTime();

    // Start interval to update every second
    const timer = setInterval(updateRemainingTime, 1000);

    // Cleanup interval when component unmounts
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className={`nc-PageContact overflow-hidden ${className}`}
      data-nc-id="PageContact"
    >
      <div className="my-24 lg:mb-32">
        <div className="container mx-auto">
          <div className="flex-shrink-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-6">
                <div className="bg-[#FFFFFF12] p-6 rounded-lg shadow-md">
                  <a href={`tel:${globalJson.phone}`}>
                    <div className="flex flex-col gap-4">
                      <svg
                        width="35"
                        height="35"
                        viewBox="0 0 35 35"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_849_464)">
                          <path
                            d="M4.08742 1.16648C4.94272 0.311061 5.93408 -0.0777712 7.06151 -4.76837e-06C8.18894 0.0777617 9.10255 0.58324 9.80233 1.51643L13.7678 6.53232C14.1565 6.99892 14.4092 7.52384 14.5259 8.10708C14.6425 8.69032 14.6231 9.25412 14.4676 9.79849L13.3013 14.5811C13.1457 15.1254 13.2624 15.592 13.6511 15.9809L19.0162 21.3467C19.4049 21.7355 19.8715 21.8522 20.4157 21.6967L25.1976 20.5302C25.7419 20.3746 26.3056 20.3552 26.8887 20.4718C27.4719 20.5885 27.9967 20.8412 28.4633 21.2301L33.4784 25.1961C34.4114 25.896 34.9168 26.8098 34.9946 27.9374C35.0723 29.065 34.6836 30.0565 33.8283 30.9119L31.6123 33.1282C30.757 33.9837 29.7656 34.5475 28.6382 34.8196C27.5108 35.0918 26.4028 35.0335 25.3142 34.6447C19.4827 32.6228 14.3704 29.4149 9.97728 25.0211C5.58419 20.6274 2.37684 15.5143 0.355239 9.68184C-0.0335305 8.59312 -0.0918459 7.48495 0.180293 6.35735C0.452431 5.22974 1.01615 4.23823 1.87144 3.38281L4.08742 1.16648ZM34.6447 0.349944C34.8779 0.505473 34.9946 0.738772 34.9946 1.04983C34.9946 1.3609 34.8779 1.63308 34.6447 1.86637L25.6641 10.965H31.7289C32.0399 10.965 32.2926 11.0622 32.487 11.2566C32.6814 11.451 32.7786 11.7037 32.7786 12.0148C32.7786 12.3259 32.6814 12.5786 32.487 12.773C32.2926 12.9674 32.0399 13.1035 31.7289 13.1813H22.9816C22.6706 13.1035 22.3985 12.9674 22.1652 12.773C21.9319 12.5786 21.8153 12.3259 21.8153 12.0148V3.26616C21.8931 2.9551 22.0291 2.70236 22.2235 2.50795C22.4179 2.31353 22.6706 2.21632 22.9816 2.21632C23.2926 2.21632 23.5453 2.31353 23.7397 2.50795C23.9341 2.70236 24.0313 2.9551 24.0313 3.26616V9.33189L33.1285 0.349944C33.3617 0.116645 33.6339 -4.76837e-06 33.9449 -4.76837e-06C34.2559 -4.76837e-06 34.4892 0.116645 34.6447 0.349944Z"
                            fill="#1675D3"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_849_464">
                            <rect
                              width="35"
                              height="34.9946"
                              fill="white"
                              transform="matrix(1 0 0 -1 0 34.9946)"
                            />
                          </clipPath>
                        </defs>
                      </svg>

                      <div className="space-y-1">
                        <p className="text-sm font-light text-[#1675D3]">
                          PHONE NUMBER
                        </p>
                        <p className="font-bold text-white">
                          {globalJson.phone}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
                <div className="bg-[#FFFFFF12] p-6 rounded-lg shadow-md">
                  <a href={`mailto:${globalJson.email}`}>
                    <div className="flex flex-col gap-4">
                      <svg
                        width="35"
                        height="36"
                        viewBox="0 0 35 36"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_849_447)">
                          <path
                            d="M19.5993 1.58844C18.8996 1.27742 18.1998 1.12191 17.5 1.12191C16.8002 1.12191 16.1004 1.27742 15.4006 1.58844L2.33799 8.58628C1.6382 8.97505 1.07449 9.51933 0.64684 10.2191C0.219194 10.9189 0.00537109 11.6576 0.00537109 12.4351V13.1349L14.7009 21.7656L17.5 20.1327L20.2991 21.7656L34.9946 13.1349V12.4351C34.9946 11.6576 34.7808 10.9189 34.3531 10.2191C33.9255 9.51933 33.3618 8.97505 32.662 8.58628L19.5993 1.58844ZM34.9946 15.7008L22.5151 22.9319L34.9946 29.9297V15.7008ZM34.878 32.379L17.5 22.6986L0.122002 32.379C0.355263 33.312 0.880102 34.0895 1.69652 34.7116C2.51293 35.3336 3.38766 35.6446 4.32071 35.6446H30.6793C31.6123 35.6446 32.487 35.3336 33.3035 34.7116C34.1199 34.0895 34.6447 33.312 34.878 32.379ZM0.00537109 29.9297L12.4849 22.9319L0.00537109 15.7008V29.9297Z"
                            fill="#1675D3"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_849_447">
                            <rect
                              width="35"
                              height="34.9892"
                              fill="white"
                              transform="matrix(1 0 0 -1 0 35.6446)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                      <div className="space-y-1">
                        <p className="text-sm font-light text-[#1675D3]">
                          EMAIL ADDRESS
                        </p>
                        <p className="font-bold text-white break-words">
                          {globalJson.email}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>

                <div className="bg-[#FFFFFF12] p-6 rounded-lg shadow-md">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      globalJson.address
                    )}`}
                  >
                    <div className="flex flex-col gap-4">
                      <svg
                        width="30"
                        height="41"
                        viewBox="0 0 30 41"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.4375 39.5295C2.03125 23.1233 0 21.4045 0 15.3108C0 7.02951 6.64062 0.31076 15 0.31076C23.2812 0.31076 30 7.02951 30 15.3108C30 21.4045 27.8906 23.1233 16.4844 39.5295C15.7812 40.6233 14.1406 40.6233 13.4375 39.5295ZM15 21.5608C18.4375 21.5608 21.25 18.8264 21.25 15.3108C21.25 11.8733 18.4375 9.06076 15 9.06076C11.4844 9.06076 8.75 11.8733 8.75 15.3108C8.75 18.8264 11.4844 21.5608 15 21.5608Z"
                          fill="#1675D3"
                        />
                      </svg>
                      <div className="space-y-1">
                        <p className="text-sm font-light text-[#1675D3]">
                          OFFICE ADDRESS
                        </p>
                        <p className="font-bold text-white">
                          {globalJson.address}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="bg-[#FFFFFF12] py-6 px-4 rounded-lg shadow-md">
                <div>
                  <div className="flex flex-col gap-4">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                    >
                      <mask
                        id="mask0_849_474"
                        style={{ maskType: "alpha" }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="40"
                        height="40"
                      >
                        <rect
                          width="40"
                          height="40"
                          fill="url(#pattern0_849_474)"
                        />
                      </mask>
                      <g mask="url(#mask0_849_474)">
                        <rect
                          x="-22.3999"
                          y="-16.8"
                          width="90.4"
                          height="68"
                          fill="#1675D3"
                        />
                      </g>
                      <defs>
                        <pattern
                          id="pattern0_849_474"
                          patternContentUnits="objectBoundingBox"
                          width="1"
                          height="1"
                        >
                          <use
                            xlinkHref="#image0_849_474"
                            transform="scale(0.0078125)"
                          />
                        </pattern>
                        <image
                          id="image0_849_474"
                          width="128"
                          height="128"
                          xlinkHref="data:image/png;base64,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"
                        />
                      </defs>
                    </svg>

                    <div>
                      <p className="text-sm font-light text-[#1675D3]">
                        OPERATING HOURS
                      </p>
                      <p className="font-semibold text-sm mt-2">
                        {isOpen ? "Open Now" : "Closed"} - {timeLeft}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm space-y-6 mt-4">
                    {days.map((day, index) => (
                      <div
                        key={index}
                        className="flex justify-between text-sm lg:text-xs xl:text-sm "
                      >
                        <span>{day}</span>
                        <span>09:00 am - 7:00 pm</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="py-4">
                {" "}
                <h2 className="text-5xl font-semibold">
                  Feel Free! You Can{" "}
                  <span className="text-primary-200">Contact</span> With Us
                </h2>
              </div>
              <form className="grid grid-cols-1 gap-6" onSubmit={sendEmail}>
                <label className="block">
                  <Input
                    placeholder="Name"
                    type="text"
                    className="mt-1"
                    value={newName}
                    onChange={(e) => {
                      setNewName(e.target.value);
                    }}
                  />
                </label>
                <label className="block">
                  <Input
                    type="email"
                    placeholder="Email"
                    className="mt-1"
                    value={getEmail}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                  />
                </label>
                <label className="block">
                  <Textarea
                    className="mt-1"
                    placeholder="Message"
                    rows={6}
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                    }}
                  />
                </label>
                <div>
                  <ButtonPrimary type="submit" className="min-w-[200px]">
                    {loading ? (
                      <div className="flex justify-center items-center col-span-full">
                        <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                      </div>
                    ) : (
                      "Send Message"
                    )}
                  </ButtonPrimary>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="container relative py-16">
        <RentalPolicies />
      </div>
    </div>
  );
};

export default PageContact;
